import React, { useContext, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import Swal from "sweetalert2";
import { GripVertical, X, CalendarDays, ShoppingBasket, Eraser, Heart } from "lucide-react";
import { ThemeContext } from "../App";
import Modal from "../components/Modal";
import Select from "../components/select/Select";
import { EmptyState } from "../components/common/Common";
import { fetchRecipeDetails } from "../api/recipes";
import { getIngredients, getRecipeTitle, toSummary } from "../utils/recipe";
import { useFavorites } from "../utils/favorites";
import { DAYS, usePlanner, useShoppingList } from "../utils/stores";
import "./ShoppingList.css";
import "./Planner.css";

const toast = (icon, title) =>
  Swal.fire({ toast: true, position: "bottom", icon, title, showConfirmButton: false, timer: 1800 });

const summaryOf = (fav) => ({ id: fav.id, title: fav.title, image: fav.image, isMealDB: fav.isMealDB });

/* ---------------------------------------------------------- Draggable bits */
const TrayChip = ({ fav }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
    id: `fav:${fav.id}`,
    data: { type: "fav", summary: summaryOf(fav) },
  });
  return (
    <div ref={setNodeRef} className={`tray-chip ${isDragging ? "dragging" : ""}`}>
      <button ref={setActivatorNodeRef} className="grip" aria-label={`Drag ${fav.title} to a day`} {...attributes} {...listeners}>
        <GripVertical size={14} />
      </button>
      <img src={fav.image} alt="" loading="lazy" />
      <span>{fav.title}</span>
    </div>
  );
};

const Entry = ({ entry, day, onView, onRemove }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
    id: `entry:${entry.uid}`,
    data: { type: "entry", day, entry },
  });
  return (
    <li ref={setNodeRef} className={`plan-entry ${isDragging ? "dragging" : ""}`}>
      <button ref={setActivatorNodeRef} className="grip" aria-label={`Move ${entry.title}`} {...attributes} {...listeners}>
        <GripVertical size={14} />
      </button>
      <img src={entry.image} alt="" loading="lazy" />
      <button className="plan-title" onClick={() => onView(entry)}>{entry.title}</button>
      <button className="plan-remove" onClick={() => onRemove(day, entry.uid)} aria-label={`Remove ${entry.title}`}>
        <X size={14} />
      </button>
    </li>
  );
};

const Day = ({ day, entries, isToday, favorites, onAdd, onView, onRemove }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${day.key}` });
  return (
    <section ref={setNodeRef} className={`plan-day ${isOver ? "over" : ""} ${isToday ? "today" : ""}`} aria-label={day.label}>
      <h2>
        {day.label}
        {isToday && <em>Today</em>}
      </h2>
      <ul>
        {entries.map((e) => (
          <Entry key={e.uid} entry={e} day={day.key} onView={onView} onRemove={onRemove} />
        ))}
      </ul>
      {entries.length === 0 && <p className="plan-empty">Drop a recipe here</p>}
      {favorites.length > 0 && (
        <Select
          className="plan-select"
          ariaLabel={`Add a recipe to ${day.label}`}
          value=""
          onChange={(id) => {
            const fav = favorites.find((f) => f.id === id);
            if (fav) onAdd(day.key, summaryOf(fav));
          }}
          options={[
            { value: "", label: "+ Add recipe" },
            ...favorites.map((f) => ({ value: f.id, label: f.title })),
          ]}
        />
      )}
    </section>
  );
};

/* -------------------------------------------------------------------- Page */
const Planner = () => {
  const { theme } = useContext(ThemeContext);
  const { favorites } = useFavorites();
  const { plan, addTo, move, removeEntry, clear } = usePlanner();
  const shopping = useShoppingList();
  const [dragging, setDragging] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [recipeData, setRecipeData] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor)
  );

  const todayKey = DAYS[(new Date().getDay() + 6) % 7].key;
  const total = DAYS.reduce((n, d) => n + (plan[d.key]?.length || 0), 0);

  const onDragStart = ({ active }) => setDragging(active.data.current);

  const onDragEnd = ({ active, over }) => {
    setDragging(null);
    if (!over || !String(over.id).startsWith("day:")) return;
    const target = String(over.id).slice(4);
    const data = active.data.current;
    if (data.type === "fav") addTo(target, data.summary);
    else if (data.type === "entry") move(data.entry.uid, data.day, target);
  };

  const view = async (item) => {
    try {
      setRecipeData(await fetchRecipeDetails(item));
      setModalOpen(true);
    } catch {
      Swal.fire("Error", "Failed to load recipe details.", "error");
    }
  };

  const addWeekToShopping = async () => {
    const unique = new Map();
    DAYS.forEach((d) => (plan[d.key] || []).forEach((e) => unique.set(e.id, e)));
    if (unique.size === 0) return;

    Swal.fire({ title: "Building your list…", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    const results = await Promise.allSettled(Array.from(unique.values()).map((e) => fetchRecipeDetails(e)));
    let added = 0;
    let failed = 0;
    results.forEach((r) => {
      if (r.status !== "fulfilled") {
        failed += 1;
        return;
      }
      const s = toSummary(r.value);
      added += shopping.addMany(s.id, getRecipeTitle(r.value), getIngredients(r.value).map((i) => i.original));
    });
    Swal.close();
    toast(
      failed ? "warning" : "success",
      failed ? `Added ${added} items (${failed} recipe${failed > 1 ? "s" : ""} couldn't load)` : `Added ${added} items to your shopping list`
    );
  };

  const confirmClear = async () => {
    const r = await Swal.fire({
      title: "Clear the whole week?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Clear plan",
    });
    if (r.isConfirmed) clear();
  };

  return (
    <main className={`planner-container ${theme ? "dark" : ""}`}>
      <header className="page-header">
        <span className="page-eyebrow">{total} meal{total === 1 ? "" : "s"} planned</span>
        <h1>Meal Planner</h1>
      </header>

      {favorites.length === 0 && total === 0 ? (
        <EmptyState
          icon={Heart}
          title="Save some favorites first"
          text="Your planner is built from your favorite recipes. Save a few, then drag them onto the days."
          action={{ label: "Find recipes", to: "/" }}
        />
      ) : (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setDragging(null)}>
          <div className="planner-bar">
            <div className="tray" aria-label="Your favorites">
              <span className="tray-label"><CalendarDays size={15} /> Drag a favorite onto a day</span>
              <div className="tray-row">
                {favorites.map((f) => (
                  <TrayChip key={f.id} fav={f} />
                ))}
              </div>
            </div>
            <div className="planner-actions">
              <button onClick={addWeekToShopping} disabled={total === 0}>
                <ShoppingBasket size={15} /> Add week to shopping list
              </button>
              <button className="danger" onClick={confirmClear} disabled={total === 0}>
                <Eraser size={15} /> Clear week
              </button>
            </div>
          </div>

          <div className="plan-grid">
            {DAYS.map((d) => (
              <Day
                key={d.key}
                day={d}
                entries={plan[d.key] || []}
                isToday={d.key === todayKey}
                favorites={favorites}
                onAdd={addTo}
                onView={view}
                onRemove={removeEntry}
              />
            ))}
          </div>

          <DragOverlay>
            {dragging ? (
              <div className="drag-ghost">
                <img src={(dragging.summary || dragging.entry).image} alt="" />
                <span>{(dragging.summary || dragging.entry).title}</span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)} recipe={recipeData} />
      )}
    </main>
  );
};

export default Planner;
